import { useCallback, useState } from "react";
import { useDropzone } from "react-dropzone";
import { FaFileUpload } from "react-icons/fa";
import { LuUpload } from "react-icons/lu";
import * as XLSX from "xlsx";
import Papa from "papaparse";

function UploadFile({ 
    setProcessedData, 
    headerRow = 3, 
    fileType = "xlsx", 
    disabled = false,
    dynamicHeaderRow = false  // 🔥 NOVA PROP
}) {
    const [dragActive, setDragActive] = useState(false);
    const [error, setError] = useState(false);
    const [fileName, setFileName] = useState("");

    // 📊 PROCESSAR EXCEL (XLSX) - COM VERIFICAÇÃO DINÂMICA DE A2
    const processExcelFile = (data) => {
        try {
            const workbook = XLSX.read(data, { type: "array" });
            const worksheet = workbook.Sheets[workbook.SheetNames[0]];

            // 🔥 VERIFICAR SE A2 ESTÁ VAZIA (APENAS SE dynamicHeaderRow = true)
            let finalHeaderRow = headerRow;
            
            if (dynamicHeaderRow) {
                const cellA2 = worksheet['A2'];
                const isA2Empty = !cellA2 || !cellA2.v || cellA2.v.toString().trim() === '';
                
                // Se A2 estiver vazia → usa linha 3 | Se A2 tiver algo → usa linha 1
                finalHeaderRow = isA2Empty ? 3 : 1;
            }

            const rows = XLSX.utils.sheet_to_json(worksheet, {
                header: 1,
                defval: "",
            });

            if (!rows || rows.length < finalHeaderRow) {
                setError(true);
                return;
            }

            const headerRowData = rows[finalHeaderRow - 1];
            const dataRows = rows.slice(finalHeaderRow);

            const formatted = dataRows.map((row) => {
                const obj = {};
                headerRowData.forEach((col, idx) => {
                    const key = col || `col_${idx}`;
                    obj[key] = row[idx] ?? "";
                });
                return obj;
            });

            setProcessedData(formatted);
            setError(false);
            
            // Reset após envio para permitir novo upload
            setTimeout(() => setFileName(""), 2000);
        } catch (err) {
            console.error('Erro ao processar Excel:', err);
            setError(true);
        }
    };

    // 📄 PROCESSAR CSV (COM DETECÇÃO AUTOMÁTICA DE DELIMITADOR)
    const processCSVFile = (text) => {
        try {
            Papa.parse(text, {
                header: true,
                skipEmptyLines: true,
                dynamicTyping: false,
                transformHeader: (header) => header.trim().replace(/['"]/g, ''),
                delimitersToGuess: [';', ',', '\t', '|'],
                complete: (results) => {
                    if (!results.data || results.data.length === 0) {
                        setError(true);
                        return;
                    }

                    const cleanData = results.data.filter(row => {
                        return Object.values(row).some(val => val !== null && val !== "");
                    });

                    if (cleanData.length === 0) {
                        setError(true);
                        return;
                    }

                    setProcessedData(cleanData);
                    setError(false);
                    
                    // Reset após envio para permitir novo upload
                    setTimeout(() => setFileName(""), 2000);
                },
                error: (err) => {
                    console.error('Erro ao processar CSV:', err);
                    setError(true);
                }
            });
        } catch (err) {
            console.error('Erro ao processar CSV:', err);
            setError(true);
        }
    };

    // 📥 HANDLE DROP
    const handleDrop = (file) => {
        if (disabled) return;

        const ext = file.name.split(".").pop().toLowerCase();
        setFileName(file.name);
        setError(false);

        if (fileType === "xlsx" && !["xlsx", "xls"].includes(ext)) {
            setError(true);
            return;
        }

        if (fileType === "csv" && ext !== "csv") {
            setError(true);
            return;
        }

        const reader = new FileReader();

        if (fileType === "xlsx") {
            reader.onload = (e) => {
                const data = new Uint8Array(e.target.result);
                processExcelFile(data);
            };
            reader.readAsArrayBuffer(file);
        } else if (fileType === "csv") {
            reader.onload = (e) => {
                let text = e.target.result;

                if (text.includes("�")) {
                    const reader2 = new FileReader();
                    reader2.onload = (ev2) => {
                        processCSVFile(ev2.target.result);
                    };
                    reader2.readAsText(file, "windows-1252");
                    return;
                }

                processCSVFile(text);
            };
            reader.readAsText(file, "utf-8");
        }

        reader.onerror = () => {
            setError(true);
        };
    };

    const onDrop = useCallback((acceptedFiles) => {
        if (disabled) return;
        
        if (acceptedFiles.length > 0) {
            handleDrop(acceptedFiles[0]);
        }
    }, [fileType, headerRow, disabled, dynamicHeaderRow]);

    // 🎯 CONFIGURAÇÃO DO DROPZONE
    const acceptedTypes = fileType === "xlsx"
        ? {
            "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet": [".xlsx"],
            "application/vnd.ms-excel": [".xls"],
        }
        : {
            "text/csv": [".csv"],
            "application/csv": [".csv"],
        };

    const { getRootProps, getInputProps } = useDropzone({
        onDrop,
        accept: acceptedTypes,
        multiple: false,
        disabled: disabled,
        onDragEnter: () => !disabled && setDragActive(true),
        onDragLeave: () => setDragActive(false),
        onDropAccepted: () => setDragActive(false),
        onDropRejected: (fileRejections) => {
            setError(true);
        }
    });

    return (
        <div className={`itenUpload ${disabled ? 'disabled' : ''}`}>
            <div
                {...getRootProps()}
                className={`dropzone ${dragActive ? "active" : ""} ${disabled ? "disabled" : ""}`}
            >
                <input {...getInputProps()} />
                <div className="upload-icon">
                    {disabled ? "🔒" : dragActive ? <FaFileUpload /> : <LuUpload />}
                </div>
            </div>
            {fileName && !disabled && (
                <p style={{ color: 'green', fontSize: '12px', marginTop: '5px' }}>
                    ✅ {fileName}
                </p>
            )}
            {error && (
                <p style={{ color: 'red', fontSize: '12px', marginTop: '5px' }}>
                    ❌ Erro ao processar {fileType.toUpperCase()}
                </p>
            )}
        </div>
    );
}

export default UploadFile;